/**
 * @fileoverview Transaction Details Modal Component
 * Displays transaction details for manual copying to external Zcash wallet
 */

import React, { useState } from 'react';
import { X, Copy, Check, AlertTriangle, ExternalLink, Shield } from 'lucide-react';
import { manualAutoSendService, type ManualAutoSendResult } from '../../services/manualAutoSendService';

interface TransactionDetails {
	recipient: string;
	amount: number;
	memo: string;
	description: string;
}

interface TransactionDetailsModalProps {
	isOpen: boolean;
	onClose: () => void;
	transactionDetails: TransactionDetails | null;
	onConfirmCopied?: () => void;
	onAutoSendSuccess?: (txid: string) => void;
}

const TransactionDetailsModal: React.FC<TransactionDetailsModalProps> = ({
	isOpen,
	onClose,
	transactionDetails,
	onConfirmCopied,
	onAutoSendSuccess
}) => {
	const SATOSHIS_PER_ZEC = 100000000;
	const [copiedItems, setCopiedItems] = useState<Set<string>>(new Set());
	const [allCopied, setAllCopied] = useState(false);
	const [mnemonic, setMnemonic] = useState('');
	const [accountIndexInput, setAccountIndexInput] = useState('0');
	const [addressIndexInput, setAddressIndexInput] = useState('0');
	const [riskAccepted, setRiskAccepted] = useState(false);
	const [autoSendStatus, setAutoSendStatus] = useState<'idle' | 'sending' | 'success'>('idle');
	const [autoSendError, setAutoSendError] = useState<string | null>(null);
	const [autoSendResult, setAutoSendResult] = useState<ManualAutoSendResult | null>(null);
	const isAutoSending = autoSendStatus === 'sending';
	const canAutoSend = Boolean(
		mnemonic.trim() &&
		riskAccepted &&
		transactionDetails &&
		!isAutoSending &&
		autoSendStatus !== 'success'
	);

	// Copy text to clipboard
	const copyToClipboard = async (text: string, item: string) => {
		try {
			await navigator.clipboard.writeText(text);
			// IMPORTANT: do the "have we copied all three?" check inside the
			// functional setter so consecutive clicks (which happen before
			// React has re-rendered) all see the up-to-date set. The earlier
			// version closed over a stale `copiedItems` ref and could miss
			// the final transition to `allCopied=true` when the test (or a
			// fast user) tapped Copy buttons in quick succession.
			const allItems = ['recipient', 'amount', 'memo'];
			setCopiedItems(prev => {
				const next = new Set([...prev, item]);
				if (allItems.every(i => next.has(i))) {
					setAllCopied(true);
				}
				return next;
			});
		} catch (error) {
			console.error('Failed to copy to clipboard:', error);
		}
	};

	// Reset state when modal closes
	const handleClose = () => {
		setCopiedItems(new Set());
		setAllCopied(false);
		setMnemonic('');
		setAccountIndexInput('0');
		setAddressIndexInput('0');
		setRiskAccepted(false);
		setAutoSendStatus('idle');
		setAutoSendError(null);
		setAutoSendResult(null);
		onClose();
	};

	// Handle confirm button
	const handleConfirm = () => {
		onConfirmCopied?.();
		handleClose();
	};

	const normaliseIndexInput = (value: string): number => {
		const parsed = Number(value);
		if (!Number.isFinite(parsed)) {
			return 0;
		}
		return parsed;
	};

	const handleAutoSend = async () => {
		if (!transactionDetails) return;

		setAutoSendError(null);
		setAutoSendResult(null);
		setAutoSendStatus('sending');

		try {
			const result = await manualAutoSendService.send({
				mnemonic,
				recipient: transactionDetails.recipient,
				amountZec: transactionDetails.amount,
				memo: transactionDetails.memo,
				accountIndex: normaliseIndexInput(accountIndexInput),
				addressIndex: normaliseIndexInput(addressIndexInput)
			});
			setAutoSendResult(result);
			setAutoSendStatus('success');
			// Drop the mnemonic from React state the moment the broadcast
			// succeeds — onAutoSendSuccess closes the modal in most flows
			// (which already wipes it via handleClose), but a parent that
			// keeps the modal open would otherwise see the phrase linger
			// in component memory until the next render.
			setMnemonic('');
			onAutoSendSuccess?.(result.txid);
		} catch (error: any) {
			setAutoSendError(error?.message || 'Automatic send failed');
			setAutoSendStatus('idle');
		}
	};

	if (!isOpen || !transactionDetails) return null;

	return (
		<div className="fixed inset-0 z-50 overflow-y-auto">
			{/* Backdrop */}
			<div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={handleClose} />
			
			{/* Modal */}
			<div className="flex items-center justify-center min-h-screen p-4">
				<div className="relative bg-white rounded-xl shadow-2xl max-w-2xl w-full">
					{/* Header */}
					<div className="bg-gradient-to-r from-purple-600 to-zcash-700 px-6 py-4 rounded-t-xl">
						<div className="flex items-center justify-between">
							<h2 className="text-xl font-bold text-white">Send shielded inscription</h2>
							<button
								onClick={handleClose}
								className="text-white hover:text-gray-200 transition-colors"
								aria-label="Close modal"
							>
								<X className="w-6 h-6" />
							</button>
						</div>
						<p className="text-purple-100 text-sm mt-2">
							Send the amount and memo from any Zcash wallet, or paste a seed phrase below to broadcast it from this browser.
						</p>
					</div>

					{/* Content */}
					<div className="p-6">
						{/* Information Banner */}
						<div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
							<div className="flex items-start space-x-3">
								<AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
								<div>
									<h4 className="text-sm font-medium text-yellow-900">How this works</h4>
									<p className="text-sm text-yellow-700 mt-1">
										{transactionDetails.description}
									</p>
									<p className="text-sm text-yellow-700 mt-2">
										The shielded memo carries the inscription metadata; without it the
										send won't be recognised. You can copy everything into Zashi / YWallet /
										Zingo, or scroll down to paste a seed phrase for one-click broadcast.
									</p>
								</div>
							</div>
						</div>

						{/* Transaction Details */}
						<div className="space-y-4">
							{/* Recipient Address */}
							<div className="bg-gray-50 rounded-lg p-4">
								<div className="flex items-center justify-between mb-2">
									<label className="text-sm font-medium text-gray-700">
										Send To Address
									</label>
									<button
										onClick={() => copyToClipboard(transactionDetails.recipient, 'recipient')}
										className="flex items-center space-x-1 text-blue-600 hover:text-blue-700 text-sm"
									>
										{copiedItems.has('recipient') ? (
											<>
												<Check className="w-4 h-4" />
												<span>Copied!</span>
											</>
										) : (
											<>
												<Copy className="w-4 h-4" />
												<span>Copy</span>
											</>
										)}
									</button>
								</div>
								<div className="bg-white border rounded p-3 font-mono text-sm break-all">
									{transactionDetails.recipient}
								</div>
							</div>

							{/* Amount */}
							<div className="bg-gray-50 rounded-lg p-4">
								<div className="flex items-center justify-between mb-2">
									<label className="text-sm font-medium text-gray-700">
										Amount (ZEC)
									</label>
									<button
										onClick={() => copyToClipboard(transactionDetails.amount.toString(), 'amount')}
										className="flex items-center space-x-1 text-blue-600 hover:text-blue-700 text-sm"
									>
										{copiedItems.has('amount') ? (
											<>
												<Check className="w-4 h-4" />
												<span>Copied!</span>
											</>
										) : (
											<>
												<Copy className="w-4 h-4" />
												<span>Copy</span>
											</>
										)}
									</button>
								</div>
								<div className="bg-white border rounded p-3 font-mono text-lg font-bold">
									{transactionDetails.amount} ZEC
								</div>
							</div>

							{/* Memo */}
							<div className="bg-gray-50 rounded-lg p-4">
								<div className="flex items-center justify-between mb-2">
									<label className="text-sm font-medium text-gray-700">
										Memo (Required)
									</label>
									<button
										onClick={() => copyToClipboard(transactionDetails.memo, 'memo')}
										className="flex items-center space-x-1 text-blue-600 hover:text-blue-700 text-sm"
									>
										{copiedItems.has('memo') ? (
											<>
												<Check className="w-4 h-4" />
												<span>Copied!</span>
											</>
										) : (
											<>
												<Copy className="w-4 h-4" />
												<span>Copy</span>
											</>
										)}
									</button>
								</div>
								<div className="bg-white border rounded p-3 font-mono text-sm max-h-32 overflow-y-auto">
									{transactionDetails.memo}
								</div>
								<p className="text-xs text-red-600 mt-2">
									⚠️ The memo is required for this transaction to work properly. Make sure to include it exactly as shown.
								</p>
							</div>
						</div>

						{/* Instructions */}
						<div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
							<h4 className="text-sm font-medium text-blue-900 mb-2">Instructions:</h4>
							<ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
								<li>Open your Zcash wallet (e.g. Zashi, YWallet, or Zingo!)</li>
								<li>Click "Send" or "Create Transaction"</li>
								<li>Paste the recipient address in the "To" field</li>
								<li>Enter the exact amount shown above</li>
								<li>Copy and paste the memo in the memo field</li>
								<li>Send the transaction</li>
							</ol>
						</div>

						{/* Wallet Links */}
						<div className="mt-4 bg-gray-50 border border-gray-200 rounded-lg p-4">
							<h4 className="text-sm font-medium text-gray-700 mb-2">Recommended Wallets (must support shielded memo):</h4>
							<div className="space-y-2">
								<a
									href="https://electriccoin.co/zashi/"
									target="_blank"
									rel="noopener noreferrer"
									className="flex items-center text-sm text-blue-600 hover:text-blue-700"
								>
									<ExternalLink className="w-4 h-4 mr-2" />
									Zashi (Desktop &amp; Mobile)
								</a>
								<a
									href="https://ywallet.app"
									target="_blank"
									rel="noopener noreferrer"
									className="flex items-center text-sm text-blue-600 hover:text-blue-700"
								>
									<ExternalLink className="w-4 h-4 mr-2" />
									YWallet (Desktop &amp; Mobile)
								</a>
								<a
									href="https://zingo.org"
									target="_blank"
									rel="noopener noreferrer"
									className="flex items-center text-sm text-blue-600 hover:text-blue-700"
								>
									<ExternalLink className="w-4 h-4 mr-2" />
									Zingo! (Desktop &amp; Mobile)
								</a>
							</div>
						</div>

						{/* Optional automatic send */}
						<div className="mt-6 bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
							<div className="flex items-start space-x-2">
								<Shield className="w-5 h-5 text-purple-600 mt-0.5" />
								<div>
									<h4 className="text-sm font-medium text-gray-900">Or sign right here with your seed phrase</h4>
									<p className="text-xs text-gray-600 mt-1">
										Paste your 12/24-word Zcash seed phrase below. The PCZT is built and
										signed locally; your phrase never leaves this page and is wiped from
										memory when you close this dialog.
									</p>
								</div>
							</div>

							<div>
								<label className="block text-xs font-medium text-gray-700 mb-1">
									Mnemonic Phrase
								</label>
								<textarea
									value={mnemonic}
									onChange={(e) => setMnemonic(e.target.value)}
									className="w-full min-h-[96px] border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-purple-500"
									placeholder="twelve or twenty-four words..."
								/>
							</div>

							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								<div>
									<label className="block text-xs font-medium text-gray-700 mb-1">Account Index</label>
									<input
										type="number"
										min={0}
										value={accountIndexInput}
										onChange={(e) => setAccountIndexInput(e.target.value)}
										className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
									/>
								</div>
								<div>
									<label className="block text-xs font-medium text-gray-700 mb-1">Address Index</label>
									<input
										type="number"
										min={0}
										value={addressIndexInput}
										onChange={(e) => setAddressIndexInput(e.target.value)}
										className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
									/>
								</div>
							</div>

							<label className="flex items-start space-x-2 text-xs text-gray-700">
								<input
									type="checkbox"
									className="mt-0.5"
									checked={riskAccepted}
									onChange={(e) => setRiskAccepted(e.target.checked)}
								/>
								<span>
									I understand this will derive a transparent key from my phrase. Keep your browser secure and only
									use this option if you trust this device.
								</span>
							</label>

							{autoSendError && (
								<div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-md p-2">
									{autoSendError}
								</div>
							)}

							{autoSendResult && (
								<div className="text-xs text-green-700 bg-green-50 border border-green-200 rounded-md p-3 space-y-1">
									<p className="font-medium">Transaction broadcast successfully.</p>
									<p>TxID: <span className="font-mono break-all">{autoSendResult.txid}</span></p>
									<p className="text-[11px] text-green-800">
										Fee: {(autoSendResult.feeSatoshis / SATOSHIS_PER_ZEC).toFixed(6)} ZEC • Change:{' '}
										{(autoSendResult.changeSatoshis / SATOSHIS_PER_ZEC).toFixed(6)} ZEC
									</p>
								</div>
							)}

							<button
								type="button"
								onClick={handleAutoSend}
								disabled={!canAutoSend}
								className={`w-full flex items-center justify-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
									canAutoSend
										? 'bg-purple-600 text-white hover:bg-purple-700'
										: 'bg-gray-300 text-gray-500 cursor-not-allowed'
								}`}
							>
								{isAutoSending ? 'Sending...' : 'Send Automatically'}
							</button>

							<p className="text-[11px] text-gray-500">
								No mnemonic data is stored or sent to the server. All computation happens locally in your browser.
							</p>
						</div>

						{/* Action Buttons */}
						<div className="mt-6 flex space-x-3">
							<button
								onClick={handleClose}
								className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
							>
								Cancel
							</button>
							<button
								onClick={handleConfirm}
								disabled={!allCopied}
								className={`flex-1 px-4 py-2 rounded-lg transition-colors flex items-center justify-center ${
									allCopied
										? 'bg-green-600 text-white hover:bg-green-700'
										: 'bg-gray-300 text-gray-500 cursor-not-allowed'
								}`}
							>
								{allCopied ? (
									<>
										<Check className="w-4 h-4 mr-2" />
										I've Copied All Details
									</>
								) : (
									'Copy All Details First'
								)}
							</button>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default TransactionDetailsModal;